/**
 * Dati e azioni del pannello "Info gruppo" (solo numeri Standard/WAHA).
 * Carica gruppo, foto e nomi (CRM prima, poi WhatsApp), resta aggiornato col broadcast
 * del core e fa le azioni di amministrazione con un esito chiaro (toast).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { WhatsAppDbService } from '@/lib/services/whatsappDbService';
import { fetchGroupParticipants } from '@/lib/hooks/whatsapp/useMentionNames';
import { formatPhoneForDisplay } from '@/lib/services/whatsapp/chatListRules';
import { isCloudService } from '@/lib/services/whatsapp/newChatLogic';
import * as G from '@/lib/services/whatsapp/wahaGroups';
import { applyParticipantsUpdate, digitsOf, isMe, myGroupRole, phoneOf, sortMembers, type GroupUpdate } from './groupLogic';
import { useGroupRealtime } from './useGroupRealtime';
import type { GroupParticipantView } from './GroupMemberItem';

export function useGroupInfo(groupId: string, fallbackName: string, isOpen: boolean) {
  const wahaConfig = useWhatsAppChatStore((s) => s.wahaConfig);
  const lidMap = useWhatsAppChatStore((s) => s.lidToCusMap);
  const session = wahaConfig?.session || '';
  const orgId = wahaConfig?.organizationId || '';
  const enabled = isOpen && !!session && !isCloudService(wahaConfig?.serviceName);

  const [details, setDetails] = useState<G.GroupDetails | null>(null);
  const [pictureUrl, setPictureUrl] = useState<string | null>(null);
  const [crmNames, setCrmNames] = useState<Map<string, string>>(new Map());
  const [waNames, setWaNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const loadNames = useCallback(async (members: G.GroupMember[]) => {
    const jids = members.map((m) => phoneOf(m, lidMap)).filter((j): j is string => !!j);
    const [crm, wa] = await Promise.all([
      orgId ? WhatsAppDbService.lookupContactNames(jids, orgId).catch(() => new Map<string, string>()) : new Map<string, string>(),
      fetchGroupParticipants(groupId).catch(() => new Map<string, string>()),
    ]);
    setCrmNames((prev) => new Map([...prev, ...crm]));
    setWaNames((prev) => new Map([...prev, ...wa]));
  }, [groupId, orgId, lidMap]);

  const load = useCallback(async (quiet = false) => {
    if (!enabled) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const d = await G.getGroup(session, groupId);
      setDetails(d);
      void loadNames(d.participants);
      if (!quiet) void G.getGroupPictureUrl(session, groupId).then(setPictureUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossibile caricare le informazioni del gruppo');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [enabled, session, groupId, loadNames]);

  useEffect(() => { void load(); }, [load]);

  useGroupRealtime(orgId, (u: GroupUpdate) => {
    if (u.groupId !== groupId) return;
    if (u.configId && wahaConfig?.configId && u.configId !== wahaConfig.configId) return;
    setDetails((d) => (d ? { ...d, subject: u.subject || d.subject, participants: applyParticipantsUpdate(d.participants, u) } : d));
    if (u.type === 'join' && u.participants?.length) {
      void loadNames(u.participants.map((p) => ({ id: p.id, phoneJid: p.id.endsWith('@c.us') ? p.id : null, role: 'participant' as const })));
    }
  }, enabled);

  const nameOf = useCallback((m: G.GroupMember) => {
    const jid = phoneOf(m, lidMap);
    const num = digitsOf(jid);
    return (jid && crmNames.get(jid)) || m.name || (num && waNames.get(num)) || (jid ? formatPhoneForDisplay(num) : 'Partecipante');
  }, [crmNames, waNames, lidMap]);

  const members = useMemo(() => details?.participants || [], [details]);
  const myRole = useMemo(() => myGroupRole(members, wahaConfig?.phoneNumber, lidMap), [members, wahaConfig?.phoneNumber, lidMap]);
  const views: GroupParticipantView[] = useMemo(() => sortMembers(members, nameOf).map((m) => {
    const jid = phoneOf(m, lidMap);
    return {
      id: m.id,
      role: m.role,
      displayName: nameOf(m),
      phoneLabel: jid ? formatPhoneForDisplay(digitsOf(jid)) : null,
      inCrm: !!(jid && crmNames.get(jid)),
      isMe: isMe(m, wahaConfig?.phoneNumber, lidMap),
    };
  }), [members, nameOf, lidMap, crmNames, wahaConfig?.phoneNumber]);

  /** Esegue un'azione: toast di esito e ricarica silenziosa. Ritorna true se riuscita. */
  const run = useCallback(async (key: string, fn: () => Promise<unknown>, okMsg: string, reload = true): Promise<boolean> => {
    setBusyKey(key);
    try {
      await fn();
      toast.success(okMsg);
      if (reload) void load(true);
      return true;
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Operazione non riuscita. Riprova tra poco.');
      return false;
    } finally {
      setBusyKey(null);
    }
  }, [load]);

  const actions = useMemo(() => ({
    rename: (subject: string) => run('subject', async () => {
      await G.setGroupSubject(session, groupId, subject);
      setDetails((d) => (d ? { ...d, subject } : d));
      useWhatsAppChatStore.setState((s) => ({
        chats: s.chats.map((c) => (c.id === groupId ? { ...c, name: subject } : c)),
        selectedChatData: s.selectedChatData?.id === groupId ? { ...s.selectedChatData, name: subject } : s.selectedChatData,
      }));
    }, 'Nome del gruppo aggiornato'),
    describe: (text: string) => run('description', async () => {
      await G.setGroupDescription(session, groupId, text);
      setDetails((d) => (d ? { ...d, description: text } : d));
    }, 'Descrizione aggiornata'),
    add: async (digits: string[]) => {
      let failed: string[] = [];
      const ok = await run('add', async () => { failed = await G.addGroupParticipants(session, groupId, digits); },
        digits.length === 1 ? 'Partecipante aggiunto' : 'Partecipanti aggiunti');
      if (ok && failed.length > 0) {
        toast.error(`${failed.length} ${failed.length === 1 ? 'numero non è stato aggiunto' : 'numeri non sono stati aggiunti'}: spesso è per le impostazioni di privacy della persona. Mandale il link d'invito.`, { duration: 8000 });
      }
      return ok;
    },
    remove: (p: GroupParticipantView) => run(`m:${p.id}`, () => G.removeGroupParticipants(session, groupId, [p.id]), `${p.displayName} rimosso dal gruppo`),
    toggleAdmin: (p: GroupParticipantView) => (p.role === 'admin'
      ? run(`m:${p.id}`, () => G.demoteGroupAdmins(session, groupId, [p.id]), `${p.displayName} non è più amministratore`)
      : run(`m:${p.id}`, () => G.promoteGroupAdmins(session, groupId, [p.id]), `${p.displayName} ora è amministratore`)),
    getInvite: () => G.getGroupInviteLink(session, groupId),
    revokeInvite: async () => {
      let link: string | null = null;
      const ok = await run('invite', async () => { link = await G.revokeGroupInviteLink(session, groupId); }, 'Link reimpostato: quello vecchio non funziona più', false);
      return ok ? (link ?? (await G.getGroupInviteLink(session, groupId).catch(() => null))) : undefined;
    },
    leave: () => run('leave', () => G.leaveGroup(session, groupId), 'Sei uscito dal gruppo', false),
  }), [run, session, groupId]);

  return {
    enabled, loading, error, reload: () => load(), busyKey, myRole, views,
    subject: details?.subject || fallbackName,
    description: details?.description || '',
    pictureUrl,
    existingDigits: members.map((m) => digitsOf(phoneOf(m, lidMap))).filter(Boolean),
    actions,
  };
}
